import type { JSX, KeyboardEvent, PointerEvent, RefObject } from 'react'
import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { drawLength, usePrefersReducedMotion } from '@/lib/motion'
import { GRID_COLOR, seriesColor } from './chart-tokens'
import {
  buildTracks,
  hourLabel,
  incidentsLabel,
  kindsLabel,
  tickStepOf,
  uptimeLabel,
  uptimeY,
  type HourTrackPoint,
  type TracksLayout
} from './hour-tracks'

/**
 * Сутки по часам: uptime площадью и инциденты столбиками на ОДНОЙ оси часов.
 *
 * Переписан целиком. Что было сломано в прошлой версии и почему это чинилось
 * только переделкой каркаса:
 *
 *  1. `preserveAspectRatio="none"` растягивал viewBox 240×H под любую ширину.
 *     Штрихи ещё спасал `vector-effect`, но текст поехал бы вместе с сеткой —
 *     поэтому подписей не было вообще: ни часов, ни процентов. Теперь компонент
 *     измеряет свою ширину и рисует 1:1, и подписи стоят прямо в SVG.
 *  2. Часы без данных просто рвали линию. Ряд, у которого статистика есть за
 *     последние 12 часов, выглядел как «график сломался на половине». Теперь
 *     пропуск — нарисованная и подписанная зона «нет данных».
 *  3. Тултип показывал одно число без единиц и без второй дорожки. Теперь
 *     курсор общий для обеих дорожек, а в подсказке uptime, число инцидентов и
 *     ИХ ВИДЫ.
 *  4. Столбики анимировались несуществующим keyframe (`apiary-grow` вместо
 *     `ui-grow-y`) — то есть не анимировались вовсе.
 *
 * Доступность: SVG — одна точка табуляции (24 focus-стопа никто не
 * пролистывает), внутри курсор двигают стрелками/Home/End, активный час
 * объявляется через `aria-live`, рядом лежит скрытая таблица с теми же числами.
 */

export interface HourTracksChartProps {
  points: HourTrackPoint[]
  /** Заголовок для скринридера и подпись скрытой таблицы. */
  label: string
  /** Код вида события → человеческое название. По умолчанию — как есть. */
  formatKind?: (kind: string) => string
  className?: string
}

/**
 * Геометрия дорожек. Подписи осей живут в отступах, поэтому `padLeft` посчитан
 * под самую длинную из них («100%» в 11px mono).
 */
const GEOMETRY = {
  padLeft: 40,
  padRight: 12,
  uptimeTop: 20,
  uptimeHeight: 100,
  barsTop: 148,
  barsHeight: 40
} as const

const AXIS_HEIGHT = 20
const HEIGHT = GEOMETRY.barsTop + GEOMETRY.barsHeight + AXIS_HEIGHT
/** Ширина до первого измерения: в jsdom и до layout'а ResizeObserver молчит. */
const FALLBACK_WIDTH = 720

const UPTIME_COLOR = 'var(--color-live)'
const INCIDENT_COLOR = seriesColor('2')

export function HourTracksChart({
  points,
  label,
  formatKind = kind => kind,
  className
}: HourTracksChartProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const width = useMeasuredWidth(hostRef, FALLBACK_WIDTH)
  const reduced = usePrefersReducedMotion()
  const hatchId = useId()
  const fadeId = useId()
  const tableId = useId()
  const [cursor, setCursor] = useState<number | null>(null)

  const layout: TracksLayout = useMemo(() => ({ width, ...GEOMETRY }), [width])
  const tracks = useMemo(() => buildTracks(points, layout), [points, layout])
  const tickStep = tickStepOf(width - GEOMETRY.padLeft - GEOMETRY.padRight, points.length)

  const active = cursor === null ? null : (points[cursor] ?? null)
  const activeBand = cursor === null ? null : (tracks.bands[cursor] ?? null)
  const barsBottom = GEOMETRY.barsTop + GEOMETRY.barsHeight
  const known = points.filter(point => point.uptimePct !== null).length

  const moveCursor = useCallback(
    (next: number): void => setCursor(Math.max(0, Math.min(points.length - 1, next))),
    [points.length]
  )

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>): void => {
    // Последний час — «сейчас»: с него удобнее начинать читать сутки назад.
    const from = cursor ?? points.length - 1
    if (event.key === 'ArrowLeft') moveCursor(from - 1)
    else if (event.key === 'ArrowRight') moveCursor(from + 1)
    else if (event.key === 'Home') moveCursor(0)
    else if (event.key === 'End') moveCursor(points.length - 1)
    else if (event.key === 'Escape') setCursor(null)
    else return
    event.preventDefault()
  }

  const onPointerMove = (event: PointerEvent<SVGSVGElement>): void => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (rect === undefined || rect.width === 0) return
    const band = tracks.bands[0]
    if (band === undefined || band.width === 0) return
    // По полосе часа, а не по ближайшей точке: под курсором именно тот час,
    // на который человек смотрит, включая часы без данных.
    moveCursor(Math.floor((event.clientX - rect.left - GEOMETRY.padLeft) / band.width))
  }

  return (
    <div ref={hostRef} className={cn('relative min-w-0', className)}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label={label}
        aria-describedby={tableId}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
        height={HEIGHT}
        className="block max-w-full rounded-xs"
        onPointerMove={onPointerMove}
        onPointerLeave={() => setCursor(null)}
        onFocus={() => setCursor(previous => previous ?? points.length - 1)}
        onBlur={() => setCursor(null)}
        onKeyDown={onKeyDown}
      >
        <defs>
          {/* Штриховка «нет данных»: заливка цветом читалась бы как значение. */}
          <pattern
            id={hatchId}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--color-line-strong)" strokeWidth="1.5" />
          </pattern>
          <linearGradient id={fadeId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={UPTIME_COLOR} stopOpacity="0.30" />
            <stop offset="100%" stopColor={UPTIME_COLOR} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        <text
          x={GEOMETRY.padLeft}
          y={GEOMETRY.uptimeTop - 8}
          className="fill-ink-muted font-sans text-2xs tracking-wide uppercase"
        >
          Uptime, %
        </text>
        <text
          x={GEOMETRY.padLeft}
          y={GEOMETRY.barsTop - 8}
          className="fill-ink-muted font-sans text-2xs tracking-wide uppercase"
        >
          Инциденты, шт
        </text>

        {/* ── дорожка uptime ───────────────────────────────────────── */}
        {[0, 25, 50, 75, 100].map(percent => {
          const y = uptimeY(percent, layout)
          return (
            <g key={percent}>
              <line
                x1={GEOMETRY.padLeft}
                x2={width - GEOMETRY.padRight}
                y1={y}
                y2={y}
                stroke={GRID_COLOR}
                strokeWidth="1"
              />
              {percent % 50 === 0 ? (
                <text
                  x={GEOMETRY.padLeft - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-ink-muted font-mono text-2xs tabular-nums"
                >
                  {percent}
                </text>
              ) : null}
            </g>
          )
        })}

        {tracks.gaps.map(gap => (
          <g key={`gap-${gap.fromHour}-${gap.hours}`}>
            <rect
              x={gap.x}
              y={GEOMETRY.uptimeTop}
              width={gap.width}
              height={GEOMETRY.uptimeHeight}
              fill={`url(#${hatchId})`}
              opacity="0.5"
            />
            {/* Подпись только там, где помещается («нет данных» — это ~60px в
                11px): в узкой зоне она вылезла бы на соседние часы. Что зона
                означает, всё равно сказано текстом под графиком. */}
            {gap.width >= 70 ? (
              <text
                x={gap.x + gap.width / 2}
                y={GEOMETRY.uptimeTop + GEOMETRY.uptimeHeight / 2 + 4}
                textAnchor="middle"
                className="fill-ink-muted font-sans text-2xs"
              >
                нет данных
              </text>
            ) : null}
          </g>
        ))}

        {tracks.runs.map((run, index) => (
          <path key={`area-${index}`} d={run.area} fill={`url(#${fadeId})`} />
        ))}
        {tracks.runs.map((run, index) => (
          <path
            key={`line-${index}`}
            d={run.line}
            fill="none"
            stroke={UPTIME_COLOR}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={reduced ? undefined : 'anim-draw'}
            style={reduced ? undefined : drawLength(width * 2)}
          />
        ))}
        {tracks.dots.map(dot => (
          <circle key={`dot-${dot.x}`} cx={dot.x} cy={dot.y} r="2.5" fill={UPTIME_COLOR} />
        ))}

        {/* ── дорожка инцидентов ───────────────────────────────────── */}
        <line
          x1={GEOMETRY.padLeft}
          x2={width - GEOMETRY.padRight}
          y1={barsBottom}
          y2={barsBottom}
          stroke={GRID_COLOR}
          strokeWidth="1"
        />
        <text
          x={GEOMETRY.padLeft - 8}
          y={GEOMETRY.barsTop + 8}
          textAnchor="end"
          className="fill-ink-muted font-mono text-2xs tabular-nums"
        >
          {tracks.incidentsTop}
        </text>
        <text
          x={GEOMETRY.padLeft - 8}
          y={barsBottom + 4}
          textAnchor="end"
          className="fill-ink-muted font-mono text-2xs tabular-nums"
        >
          0
        </text>

        {tracks.bars.map(bar => (
          <rect
            key={`bar-${bar.index}`}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            rx="2"
            fill={INCIDENT_COLOR}
            opacity={cursor === null || cursor === bar.index ? 1 : 0.55}
            style={
              reduced
                ? undefined
                : {
                    transformBox: 'fill-box',
                    transformOrigin: 'bottom',
                    animation: 'ui-grow-y var(--t-chart) var(--ease-out-expo) both',
                    animationDelay: `${bar.index * 12}ms`
                  }
            }
          />
        ))}

        {/* ── ось часов ────────────────────────────────────────────── */}
        {tracks.bands.map(band => {
          const point = points[band.index]
          if (point === undefined || band.index % tickStep !== 0) return null
          return (
            <text
              key={`tick-${band.index}`}
              x={band.center}
              y={barsBottom + 15}
              textAnchor="middle"
              className="fill-ink-muted font-mono text-2xs tabular-nums"
            >
              {String(point.hour).padStart(2, '0')}
            </text>
          )
        })}

        {/* ── курсор: одна вертикаль на обе дорожки ────────────────── */}
        {active === null || activeBand === null ? null : (
          <>
            <rect
              x={activeBand.x}
              y={GEOMETRY.uptimeTop}
              width={activeBand.width}
              height={barsBottom - GEOMETRY.uptimeTop}
              fill="var(--color-ink)"
              opacity="0.05"
            />
            <line
              x1={activeBand.center}
              x2={activeBand.center}
              y1={GEOMETRY.uptimeTop}
              y2={barsBottom}
              stroke="var(--color-ink-muted)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            {active.uptimePct === null ? null : (
              <circle
                cx={activeBand.center}
                cy={uptimeY(active.uptimePct, layout)}
                r="3.5"
                fill="var(--color-panel)"
                stroke={UPTIME_COLOR}
                strokeWidth="2"
              />
            )}
          </>
        )}
      </svg>

      {/* Подсказка. HTML, а не SVG-текст: тот не переносится и не масштабируется. */}
      {active === null || activeBand === null ? null : (
        <div
          role="status"
          aria-live="polite"
          style={{ left: clampTooltip(activeBand.center, width) }}
          className="pointer-events-none absolute top-0 z-popover w-max max-w-56 -translate-x-1/2 rounded-sm border border-line bg-panel-2 px-2.5 py-1.5 shadow-md"
        >
          <p className="font-mono text-2xs text-ink-muted tabular-nums">
            {hourLabel(active.hour)} UTC
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-ink">
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-pill"
              style={{ background: UPTIME_COLOR }}
            />
            Uptime <span className="font-mono tabular-nums">{uptimeLabel(active.uptimePct)}</span>
          </p>
          <p className="mt-0.5 flex items-start gap-1.5 text-xs text-ink">
            <span
              aria-hidden
              className="mt-1.5 size-2 shrink-0 rounded-pill"
              style={{ background: INCIDENT_COLOR }}
            />
            <span className="min-w-0">
              {incidentsLabel(active.incidents)}
              {active.kinds.length === 0 ? null : (
                <span className="block text-2xs text-ink-muted">
                  {kindsLabel(active.kinds, formatKind)}
                </span>
              )}
            </span>
          </p>
        </div>
      )}

      {/* Сколько часов вообще посчитано: иначе штриховка выглядит поломкой. */}
      <p className="mt-1 text-2xs text-ink-muted">
        {known === points.length
          ? 'Статистика есть за все 24 часа.'
          : `Статистика есть за ${known} из ${points.length} часов; остальные заштрихованы — за них данных нет.`}
      </p>

      {/* Табличная альтернатива: график не единственный способ прочитать ряд. */}
      <table id={tableId} className="sr-only">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Час, UTC</th>
            <th scope="col">Uptime</th>
            <th scope="col">Инциденты</th>
            <th scope="col">Виды инцидентов</th>
          </tr>
        </thead>
        <tbody>
          {points.map(point => (
            <tr key={point.hour}>
              <th scope="row">{hourLabel(point.hour)}</th>
              <td>{uptimeLabel(point.uptimePct)}</td>
              <td>{incidentsLabel(point.incidents)}</td>
              <td>{kindsLabel(point.kinds, formatKind, point.kinds.length)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Подсказка не должна вылезать за график: у краёв она прижимается к кромке. */
function clampTooltip(center: number, width: number): number {
  const half = 84
  return Math.max(half, Math.min(width - half, center))
}

/**
 * Ширина хоста в пикселях.
 *
 * Нужна потому, что график рисуется 1:1 (см. шапку модуля `hour-tracks`).
 *
 * Слушателей два, и это не перестраховка ради перестраховки:
 *  - `ResizeObserver` ловит изменения, которых окно не видит: свёрнутый
 *    сайдбар, появившуюся колонку, скроллбар;
 *  - `resize` окна — запасной путь для сред, где observer не работает
 *    (в jsdom его нет вовсе, и там остаётся запасная ширина; без неё ряд
 *    схлопнулся бы в нулевую и проверять в тестах было бы нечего).
 */
function useMeasuredWidth(ref: RefObject<HTMLElement | null>, fallback: number): number {
  const [width, setWidth] = useState(fallback)

  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) return
    const measure = (): void => {
      const next = Math.round(element.clientWidth)
      // Ноль — это «элемент ещё не в раскладке»: перерисовывать по нему нельзя.
      if (next > 0) setWidth(previous => (previous === next ? previous : next))
    }
    measure()

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(element)
    window.addEventListener('resize', measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [ref])

  return width
}

/** Реэкспорт формы точки: экраны собирают её, но геометрию знать не обязаны. */
export type { HourTrackPoint }
